import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Table, Form, Dropdown, ButtonGroup, Badge } from "react-bootstrap";
import Swal from "sweetalert2";
import db from "../../../../offline/db";
import { useAuth } from "../../../inicio/context/AuthContext";
import { useOnlineStatus } from "../../../inicio/hooks/useSync";
import { getPersonas, deletePersona, exportarExcelMigrante } from "../api/MigranteAPI";
import { sincronizarMigrantePendientes, eliminarOfflineMigrante } from "../offline/migranteOffline";
import MigranteModal from "../modal/MigranteModal";
import ModalConsentimientoMigrante from "../modal/ModalConsentimientoMigrante";
import MapaMigrante from "../components/MapaMigrante";
import { ESTRATEGIA_COLOR } from "../../../dashboard/utils/colores";

const REGISTROS_POR_PAGINA = 10;
const INTERVALO_POLLING_MS = 30000;

const AZUL = "#1a3a5c";
const TH = { padding: "11px 14px", fontWeight: 600, whiteSpace: "nowrap", fontSize: 12, border: "none" };
const TD = { padding: "10px 12px", verticalAlign: "middle", fontSize: 13 };
const BTN_ICONO = { background: "none", border: "none", cursor: "pointer", fontSize: 15, marginRight: 4 };

function puedeEliminar(persona, user) {
  if (user?.rol === "admin") return true;
  if (persona._local) return true;
  return persona.creado_por_id != null && persona.creado_por_id === user?.id;
}

export default function TablaMigrante() {
  const online = useOnlineStatus();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [lista, setLista] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [personaEdit, setPersonaEdit] = useState(null);
  const [showConsentimiento, setShowConsentimiento] = useState(false);
  const [personaConsentimiento, setPersonaConsentimiento] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [territorioFiltro, setTerritorioFiltro] = useState("");
  const [comunidadFiltro, setComunidadFiltro] = useState("");
  const [pagina, setPagina] = useState(1);
  const [modoOffline, setModoOffline] = useState(!navigator.onLine);
  const [cargando, setCargando] = useState(true);
  const [actualizadoEn, setActualizadoEn] = useState(null);
  const [vista, setVista] = useState("tabla");
  const [personaFoco, setPersonaFoco] = useState(null);

  const cargandoRef = useRef(false);

  const territoriosConDatos = useMemo(() => {
    return Array.from(new Set(lista.map(p => p.municipio).filter(Boolean))).sort();
  }, [lista]);

  const comunidadesConDatos = useMemo(() => {
    const base = territorioFiltro ? lista.filter(p => p.municipio === territorioFiltro) : lista;
    return Array.from(new Set(base.map(p => p.nombre_comunidad).filter(Boolean))).sort();
  }, [lista, territorioFiltro]);

  const loadData = async (silencioso = false) => {
    if (cargandoRef.current) return;
    cargandoRef.current = true;
    if (!silencioso) setCargando(true);

    if (!navigator.onLine) {
      setModoOffline(true);
      try {
        const cache = await db.migrante_cache.toArray();
        const pendientes = await db.migrante_pendientes.filter(r => r.sincronizado === 0).toArray();
        const idsConEliminacionPendiente = new Set(pendientes.filter(r => r._tipo === "eliminar").map(r => r._editId));
        const cacheVisible = cache.filter(c => !idsConEliminacionPendiente.has(c.id));
        const pendientesVisibles = pendientes
          .filter(r => r._tipo !== "eliminar")
          .map(r => ({ ...r, id: `local_${r.id}`, _local: true }));
        setLista([...cacheVisible, ...pendientesVisibles]);
      } catch (e) { console.error("❌ Error offline:", e); }
      finally { setCargando(false); cargandoRef.current = false; }
      return;
    }

    try {
      const res = await getPersonas();
      setModoOffline(false);
      setLista(res.data);
      setActualizadoEn(new Date());
      const serializado = JSON.parse(JSON.stringify(res.data));
      await db.transaction("rw", db.migrante_cache, async () => {
        await db.migrante_cache.clear();
        await db.migrante_cache.bulkAdd(serializado);
      });
    } catch (error) {
      console.error("❌ Error cargando registros:", error);
      const esErrorDeRed = !error.response;
      if (error.response?.status === 401) {
        Swal.fire({ icon:"warning", title:"Tu sesión expiró", text:"Vuelve a iniciar sesión para continuar.", confirmButtonColor:"#1a3a5c" })
          .then(() => { logout(); navigate("/login"); });
        return;
      }
      if (esErrorDeRed) {
        setModoOffline(true);
        try { const cache = await db.migrante_cache.toArray(); if (cache.length > 0) setLista(cache); } catch (e) { console.error(e); }
      } else if (!silencioso) {
        Swal.fire({ icon:"error", title:"No se pudieron cargar los registros", text: error.response?.data?.message || "Intenta de nuevo.", confirmButtonColor:"#1a3a5c" });
      }
    } finally { setCargando(false); cargandoRef.current = false; }
  };

  const eliminar = async (id, nombre) => {
    const c = await Swal.fire({ icon:"warning", title:"¿Eliminar este registro?", text: nombre, showCancelButton:true, confirmButtonText:"Sí, eliminar", cancelButtonText:"Cancelar", confirmButtonColor:"#dc3545" });
    if (!c.isConfirmed) return;

    const estaOnline = online && navigator.onLine;

    if (!estaOnline) {
      try {
        await eliminarOfflineMigrante(id);
        await Swal.fire({ icon:"info", title:"📴 Se eliminará al reconectar", timer:1500, showConfirmButton:false });
        loadData();
      } catch (error) {
        console.error(error);
        Swal.fire({ icon:"error", title:"No se pudo guardar la eliminación", confirmButtonColor:"#1a3a5c" });
      }
      return;
    }

    try {
      await deletePersona(id);
      loadData();
    } catch (error) {
      console.error(error);
      if (!error.response) {
        try {
          await eliminarOfflineMigrante(id);
          await Swal.fire({ icon:"info", title:"📴 Sin conexión — se eliminará al reconectar", timer:1800, showConfirmButton:false });
          loadData();
          return;
        } catch (e2) { console.error(e2); }
      }
      Swal.fire({ icon:"error", title:"No se pudo eliminar", text: error.response?.data?.message || "", confirmButtonColor:"#1a3a5c" });
    }
  };

  const descargarExcel = async () => {
    try {
      const res = await exportarExcelMigrante();
      let filename = "Migrante.xlsx";
      const disposition = res.headers["content-disposition"];
      if (disposition?.includes("filename=")) filename = disposition.split("filename=")[1].replace(/"/g, "");
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
    } catch (error) { console.error(error); Swal.fire({ icon:"error", title:"No se pudo exportar", confirmButtonColor:"#1a3a5c" }); }
  };

  const listaFiltrada = lista.filter(p => {
    const coincideTexto = p.primer_nombre?.toLowerCase().includes(busqueda.toLowerCase()) || p.documento?.toLowerCase().includes(busqueda.toLowerCase());
    const coincideTerritorio = !territorioFiltro || p.municipio === territorioFiltro;
    const coincideComunidad = !comunidadFiltro || p.nombre_comunidad === comunidadFiltro;
    return coincideTexto && coincideTerritorio && coincideComunidad;
  });
  const totalPags = Math.ceil(listaFiltrada.length / REGISTROS_POR_PAGINA) || 1;
  const paginados = listaFiltrada.slice((pagina - 1) * REGISTROS_POR_PAGINA, pagina * REGISTROS_POR_PAGINA);

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setModoOffline(!online); }, [online]);
  useEffect(() => { if (!online) return; sincronizarMigrantePendientes().then(() => loadData()); }, [online]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onSync = () => loadData();
    window.addEventListener("sync-completado", onSync);
    return () => window.removeEventListener("sync-completado", onSync);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!online) return;
    const intervalo = setInterval(() => loadData(true), INTERVALO_POLLING_MS);
    return () => clearInterval(intervalo);
  }, [online]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible" && online) loadData(true); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [online]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPagina(1); }, [busqueda, territorioFiltro, comunidadFiltro]);
  useEffect(() => { setComunidadFiltro(""); }, [territorioFiltro]);

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }} className="py-3">

      <div style={{
        background: "linear-gradient(135deg, #1a3a5c 0%, #2d5986 60%, #3a6ea5 100%)", borderLeft: `6px solid ${ESTRATEGIA_COLOR.migrante}`, boxShadow: "0 10px 26px rgba(26,58,92,.18)",
        borderRadius: 16, padding: "20px 24px", marginBottom: 20, color: "#fff", position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", right: -30, top: -30, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
        <div style={{ fontSize: 11, opacity: 0.75, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 3 }}>
          🧭 Población de Alto Valor Constitucional · 2026
        </div>
        <h5 style={{ margin: 0, fontWeight: 800, fontSize: "1.15rem" }}>Población Migrante</h5>
        <div style={{ marginTop: 5, fontSize: 13, opacity: 0.85 }}>
          Secretaría de Salud del Amazonas
          {actualizadoEn && !modoOffline && <> · actualizado {actualizadoEn.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}</>}
        </div>
      </div>

      {modoOffline && (
        <div className="alert alert-warning py-2 px-3 mb-3 d-flex align-items-center gap-2" style={{ borderRadius: 10, fontSize: "0.85rem" }}>
          <span>📴</span>
          <div><strong>Modo sin conexión</strong> — {lista.length} registro(s) local(es). Se sincronizarán al reconectarse.</div>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <Form.Control placeholder="🔎 Buscar nombre o documento..." value={busqueda} onChange={e => setBusqueda(e.target.value)} style={{ maxWidth: 260, fontSize: 13 }} />
        <Form.Select value={territorioFiltro} onChange={e => setTerritorioFiltro(e.target.value)} style={{ maxWidth: 220, fontSize: 13 }}>
          <option value="">Todos los territorios</option>
          {territoriosConDatos.map(t => <option key={t} value={t}>{t}</option>)}
        </Form.Select>
        <Form.Select value={comunidadFiltro} onChange={e => setComunidadFiltro(e.target.value)} style={{ maxWidth: 220, fontSize: 13 }}>
          <option value="">Todas las comunidades</option>
          {comunidadesConDatos.map(c => <option key={c} value={c}>{c}</option>)}
        </Form.Select>
        <Button variant={vista === "mapa" ? "primary" : "outline-primary"}
          onClick={() => { setVista(v => v === "mapa" ? "tabla" : "mapa"); setPersonaFoco(null); }}
          style={vista === "mapa" ? { background: AZUL, border: "none", fontSize: 13 } : { fontSize: 13 }}>
          {vista === "mapa" ? "📋 Ver tabla" : "🗺️ Ver mapa"}
        </Button>
        <Dropdown as={ButtonGroup}>
          <Dropdown.Toggle variant="outline-success" style={{ fontSize: 13, fontWeight: 600 }}>📥 Excel</Dropdown.Toggle>
          <Dropdown.Menu><Dropdown.Item onClick={descargarExcel}>Descargar todo</Dropdown.Item></Dropdown.Menu>
        </Dropdown>
        <div style={{ marginLeft: "auto" }}>
          <Button onClick={() => { setPersonaEdit(null); setShowModal(true); }}
            style={{ background: "linear-gradient(135deg, #1a3a5c, #2d5986)", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13, padding: "8px 18px" }}>
            ➕ Nuevo registro
          </Button>
        </div>
      </div>

      {vista === "mapa" ? (
        <MapaMigrante registros={listaFiltrada} foco={personaFoco} />
      ) : cargando ? (
        <div style={{ textAlign: "center", padding: 60, color: "#6c757d" }}><div style={{ fontSize: "2rem", marginBottom: 8 }}>⏳</div>Cargando registros...</div>
      ) : listaFiltrada.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", background: "#fff", borderRadius: 16, border: "2px dashed #dee2e6" }}>
          <div style={{ fontSize: "3rem", marginBottom: 10 }}>🧭</div>
          <div style={{ fontWeight: 600, color: "#495057" }}>{lista.length === 0 ? "Sin registros todavía" : "Sin resultados para este filtro"}</div>
          {lista.length === 0 && <div style={{ color: "#adb5bd", fontSize: 13, marginTop: 4 }}>Usa "Nuevo registro" para empezar</div>}
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,.06)" }}>
          <div style={{ overflowX: "auto" }}>
            <Table hover style={{ margin: 0, fontSize: 13, minWidth: 900 }}>
              <thead>
                <tr style={{ background: AZUL, color: "#fff" }}>
                  <th style={TH}>#</th>
                  <th style={{ ...TH, minWidth: 190 }}>Persona</th>
                  <th style={TH}>Documento</th>
                  <th style={TH}>Edad</th>
                  <th style={TH}>Género</th>
                  <th style={{ ...TH, minWidth: 140 }}>País origen</th>
                  <th style={{ ...TH, minWidth: 130 }}>Situación migratoria</th>
                  <th style={{ ...TH, textAlign: "center" }}>Estado</th>
                  <th style={{ ...TH, textAlign: "center" }} title="Consentimiento informado">Doc.</th>
                  <th style={{ ...TH, textAlign: "center" }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {paginados.map((p, i) => (
                  <tr key={p.id} style={{ borderBottom: "1px solid #f1f3f5" }}>
                    <td style={{ ...TD, color: "#adb5bd", fontWeight: 600 }}>{(pagina - 1) * REGISTROS_POR_PAGINA + i + 1}</td>
                    <td style={TD}>
                      <div style={{ fontWeight: 600, color: AZUL, display: "flex", alignItems: "center", gap: 6 }}>
                        {`${p.primer_nombre || ""} ${p.primer_apellido || ""}`}
                        {p._local && <span title="Pendiente de sincronizar" style={{ background: "#FAEEDA", color: "#854F0B", border: "1px solid #EF9F27", borderRadius: 20, padding: "1px 7px", fontSize: 9, fontWeight: 700 }}>⏳</span>}
                      </div>
                      <div style={{ fontSize: 11, color: "#6b7280" }}>{p.municipio || "—"}</div>
                    </td>
                    <td style={TD}><span style={{ background: "#f1f5f9", borderRadius: 5, padding: "2px 7px", fontFamily: "monospace", fontSize: 12 }}>{p.documento}</span></td>
                    <td style={TD}>{p.edad_anios ? `${p.edad_anios}a` : "—"}</td>
                    <td style={TD}>
                      <span style={{
                        fontSize: "0.72rem", fontWeight: 700, padding: "2px 9px", borderRadius: 20,
                        background: p.genero === "Masculino" ? "#e8f0fb" : p.genero === "Femenino" ? "#fce8f1" : "#f1f5f9",
                        color: p.genero === "Masculino" ? "#2e5a8c" : p.genero === "Femenino" ? "#b5457a" : "#6c757d",
                      }}>{p.genero || "—"}</span>
                    </td>
                    <td style={{ ...TD, fontSize: 12 }}>{p.pais_origen || "—"}</td>
                    <td style={{ ...TD, fontSize: 12 }}>{p.situacion_migratoria || "—"}</td>
                    <td style={{ ...TD, textAlign: "center" }}>{p._local ? <Badge bg="warning" text="dark">⏳</Badge> : <Badge bg="success">✅</Badge>}</td>
                    <td style={{ ...TD, textAlign: "center" }}>
                      <span title={p.consentimiento_archivo_url ? `Consentimiento: ${p.consentimiento_archivo_nombre || "cargado"}` : "Sin consentimiento informado"}
                        style={{ fontSize: 13, color: p.consentimiento_archivo_url ? "#198754" : "#adb5bd" }}>
                        ✍️{p.consentimiento_archivo_url ? "✅" : "❌"}
                      </span>
                    </td>
                    <td style={{ ...TD, textAlign: "center", whiteSpace: "nowrap" }}>
                      <button style={BTN_ICONO} title="Editar" onClick={() => { setPersonaEdit(p); setShowModal(true); }}>✏️</button>
                      <button style={BTN_ICONO} title={p.consentimiento_archivo_url ? "Ver / reemplazar consentimiento" : "Cargar consentimiento"}
                        onClick={() => { setPersonaConsentimiento(p); setShowConsentimiento(true); }} disabled={p._local}>✍️</button>
                      {p.latitud && p.longitud && (
                        <button style={BTN_ICONO} title="Ver en mapa" onClick={() => { setPersonaFoco(p); setVista("mapa"); }}>🗺️</button>
                      )}
                      {puedeEliminar(p, user) && (
                        <button style={BTN_ICONO} title="Eliminar" onClick={() => eliminar(p.id, `${p.primer_nombre} ${p.primer_apellido}`)}>🗑️</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div style={{ padding: "10px 20px", background: "#f8faf8", borderTop: "1px solid #e9ecef", fontSize: 12, color: "#6c757d", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <span>Mostrando <strong>{paginados.length}</strong> de <strong>{listaFiltrada.length}</strong> · Total: <strong>{lista.length}</strong></span>
            {totalPags > 1 && (
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1}
                  style={{ border: "1px solid #dee2e6", background: pagina === 1 ? "#f8f9fa" : "#fff", borderRadius: 6, padding: "3px 10px", cursor: pagina === 1 ? "not-allowed" : "pointer", fontSize: 12 }}>◀</button>
                <span style={{ fontWeight: 700 }}>{pagina}/{totalPags}</span>
                <button onClick={() => setPagina(p => Math.min(totalPags, p + 1))} disabled={pagina === totalPags}
                  style={{ border: "1px solid #dee2e6", background: pagina === totalPags ? "#f8f9fa" : "#fff", borderRadius: 6, padding: "3px 10px", cursor: pagina === totalPags ? "not-allowed" : "pointer", fontSize: 12 }}>▶</button>
              </div>
            )}
          </div>
        </div>
      )}

      <MigranteModal show={showModal} onClose={() => { setShowModal(false); setPersonaEdit(null); }} reload={loadData} persona={personaEdit} />
      <ModalConsentimientoMigrante show={showConsentimiento} onClose={() => { setShowConsentimiento(false); setPersonaConsentimiento(null); }} persona={personaConsentimiento} reload={loadData} />
    </div>
  );
}
